import React, { useContext, useState } from "react";
import { showSuccessAlert, showErrorAlert } from "../utils/alertUtils";
import { API_BASE_URL } from "../environment";
import { AuthContext } from "../context/authcontext";
import { useIsSalesUser } from "../utils/salesUserScope";

export type ExportEntityType =
  | "contact"
  | "company"
  | "deal"
  | "lead"
  | "pipeline"
  | "proposal"
  | "contract";

interface ExportComponentProps {
  entityType: ExportEntityType;
  filters?: Record<string, any>;
  format?: "pdf" | "excel";
  className?: string;
}

const SEARCH_FIELDS: Record<ExportEntityType, string> = {
  company: "company_name",
  contact: "contact_full_name",
  lead: "title",
  pipeline: "title",
  deal: "title",
  proposal: "subject",
  contract: "subject",
};

const STATUS_FIELDS: Partial<Record<ExportEntityType, string>> = {
  lead: "lifecycle_type_stage_id",
  pipeline: "lifecycle_type_stage_id",
  deal: "lifecycle_type_stage_id",
  proposal: "proposal_status_id",
  contract: "contract_type_id",
};

const ARCHIVABLE_ENTITIES: ExportEntityType[] = [
  "lead",
  "pipeline",
  "deal",
  "proposal",
  "contract",
];

const RESERVED_KEYS = [
  "searchTerm",
  "dateRange",
  "statusFilter",
  "archivedFilter",
  "sortBy",
  "sortOrder",
  "industryFilter",
  "countryFilter",
];

const ALIASED_KEYS: Record<string, string> = {
  teamFilter: "assigned_team_id",
  userFilter: "assigned_user_id",
  sourceFilter: "source",
};

const isEmptyValue = (value: any) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const formatDate = (date: Date) => date.toISOString().split("T")[0];

const checkSalesUserRole = (): {
  isClientAdmin: boolean;
  userId: number | null;
} => {
  let isClientAdmin = false;
  let userId = null;

  const authResponse = localStorage.getItem("authResponse");

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      const tenantId = localStorage.getItem("selectedTenantId");
      const tenantObj = Array.isArray(auth.tenants)
        ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
        : null;

      isClientAdmin = tenantObj?.role_key === "CLIENTADMIN";
      userId = auth.user_id || null;
    } catch (e) {
      console.warn("Could not parse auth response to check for user role", e);
    }
  }

  return { isClientAdmin, userId };
};

export const buildExportParams = (
  entityType: ExportEntityType,
  tenantId: string,
  filters: Record<string, any> = {}
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("tenant_id", tenantId);

  const { isClientAdmin, userId } = checkSalesUserRole();
  if (!isClientAdmin && userId) {
    params.set("sales_user_id", String(userId));
  }

  if (ARCHIVABLE_ENTITIES.includes(entityType)) {
    const archivedFilter = filters.archivedFilter || "no";
    if (archivedFilter === "yes") {
      params.set("is_archive", "true");
    } else if (archivedFilter === "no") {
      params.set("is_archive", "false");
    }
  }

  if (filters.searchTerm) {
    params.set(SEARCH_FIELDS[entityType], filters.searchTerm);
  }

  const [startDate, endDate] = Array.isArray(filters.dateRange)
    ? filters.dateRange
    : [null, null];
  if (startDate instanceof Date) {
    params.set("created_datetime_gte", formatDate(startDate));
  }
  if (endDate instanceof Date) {
    params.set("created_datetime_lte", formatDate(endDate));
  }

  const statusField = STATUS_FIELDS[entityType];
  if (statusField && !isEmptyValue(filters.statusFilter)) {
    params.set(statusField, String(filters.statusFilter));
  }

  if (entityType === "company" && Array.isArray(filters.industryFilter)) {
    filters.industryFilter.forEach((industry: string) =>
      params.append("industry_type_display_name", industry)
    );
  }

  if (
    (entityType === "company" || entityType === "contact") &&
    Array.isArray(filters.countryFilter)
  ) {
    filters.countryFilter.forEach((country: string) =>
      params.append("country", country)
    );
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (RESERVED_KEYS.includes(key) || isEmptyValue(value)) return;
    const paramKey = ALIASED_KEYS[key] || key;
    if (Array.isArray(value)) {
      params.delete(paramKey);
      value.forEach((item) => params.append(paramKey, String(item)));
    } else {
      params.set(paramKey, String(value));
    }
  });

  if (entityType === "contact" && !params.has("is_active")) {
    params.set("is_active", "true");
  }

  return params;
};

const ExportComponent: React.FC<ExportComponentProps> = ({
  entityType,
  filters,
  format = "excel",
  className = "btn btn-outline-primary me-2",
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const isSalesUser = useIsSalesUser();
  const [isExporting, setIsExporting] = useState(false);

  if (isSalesUser) return null;

  const handleExport = async () => {
    if (!selectedTenantId) {
      await showErrorAlert("Tenant ID not available");
      return;
    }

    setIsExporting(true);
    try {
      const authCred = localStorage.getItem("authCred");
      if (!authCred) {
        throw new Error("Authentication required. Please login first.");
      }

      const params = buildExportParams(entityType, selectedTenantId, filters);
      const url = `${API_BASE_URL}/${entityType}/export/?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Basic ${authCred}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success || !data.download_url) {
        throw new Error(
          data.message || "Export failed - no download URL provided"
        );
      }

      const downloadResponse = await fetch(data.download_url);
      if (!downloadResponse.ok) {
        throw new Error(`Failed to download file from ${data.download_url}`);
      }

      const blob = await downloadResponse.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      const urlParts = data.download_url.split("/");
      link.download =
        urlParts[urlParts.length - 1] ||
        `${entityType}s_export_${formatDate(new Date())}.${
          format === "pdf" ? "pdf" : "xlsx"
        }`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      await showSuccessAlert(
        `${
          data.message ||
          `${
            entityType.charAt(0).toUpperCase() + entityType.slice(1)
          }s data exported successfully as ${format.toUpperCase()}`
        } (${data.record_count || 0} records)`
      );
    } catch (error) {
      console.error("Export error:", error);
      await showErrorAlert(
        error instanceof Error
          ? error.message
          : `Failed to export ${entityType}s data`
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleExport}
      disabled={isExporting}
      style={{ opacity: isExporting ? 0.6 : 1 }}
    >
      <i
        className={`ti ${
          isExporting ? "ti-loader ti-spin" : "ti-package-export"
        } me-2`}
      />
      {isExporting ? "Exporting..." : "Export"}
    </button>
  );
};

export default ExportComponent;
